import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import {
  checkResellerChipAccessQuery,
  assignExistingPetToChipMutation,
} from '@/appview/RegisterChipV2/RegisterChip.graphql';
import { reassignChipToResellerMutation } from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import { normalizePhoneDigits } from '../../lib/utils';
import styles from './AddChipToPetModal.module.scss';

interface PetInfo {
  id: string;
  name: string;
  breed?: { name: string } | null;
  /**
   * When set, the pet is mid-adoption and the chip's existing contact (if any)
   * must match this adopter by email or phone before we'll touch the chip.
   * Guards against accidentally clearing a chip that belongs to someone other
   * than the adopter currently being invited.
   */
  pendingAdoption?: {
    fullName: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

interface AddChipToPetModalProps {
  open: boolean;
  onClose: () => void;
  pet: PetInfo | null;
}

export default function AddChipToPetModal({ open, onClose, pet }: AddChipToPetModalProps) {
  const { showToast } = useToast();
  const [serialNumber, setSerialNumber] = useState('');
  const [serialError, setSerialError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [checkChipAccess] = useLazyQuery(checkResellerChipAccessQuery);
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, {
    refetchQueries: 'active' as const,
  });
  const [assignExistingPet] = useMutation(assignExistingPetToChipMutation, {
    refetchQueries: 'active' as const,
  });

  function handleClose() {
    setSerialNumber('');
    setSerialError('');
    setSubmitting(false);
    onClose();
  }

  async function handleSubmit() {
    if (!pet) {
      return;
    }
    // Serial Number is only digits, so doesn't need case insensitivity.
    const trimmed = serialNumber.trim();
    if (!trimmed) {
      setSerialError('Serial number is required');
      return;
    }
    setSubmitting(true);
    setSerialError('');
    try {
      // Step 1: Validate chip and determine ownership
      const { data, error } = await checkChipAccess({ variables: { id: trimmed } });
      if (error || !data?.resellerChip) {
        setSerialError('Chip not found in your inventory.');
        return;
      }
      const graphqlId = data.resellerChip.id;
      const chipResellerId = data.resellerChip.reseller?.id;
      const userResellerId = data.currentUser?.chipReseller?.id;
      const needsClaim = !chipResellerId || chipResellerId !== userResellerId;

      // Step 2: For a pending-adoption pet, refuse to touch a chip whose
      // existing owner doesn't match the adopter. Either contact (email or
      // phone) is enough to confirm same person across slightly different
      // form fills; when the chip has no contact entry there's nothing to
      // gate on. The `adopterHasIdentifier` branch is a defensive guard
      // against malformed/legacy transfer rows — `rescueCreatePetAdoptionTransfer`
      // requires `phoneNumber` upstream (rescueResolvers.ts:851-854), so any
      // adoption created through the rescue portal will always have a phone
      // to compare against.
      const existingContact = data.resellerChip.latestContactHistoryEntry?.contactInfo;
      const adopter = pet.pendingAdoption;
      const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();
      const adopterEmail = adopter ? norm(adopter.email) : '';
      const adopterPhone = adopter ? normalizePhoneDigits(adopter.phone) : '';
      const adopterHasIdentifier = !!adopterEmail || !!adopterPhone;
      if (adopter && existingContact && adopterHasIdentifier) {
        const emailMatches = !!adopterEmail && adopterEmail === norm(existingContact.primaryEmail);
        const phoneMatches = !!adopterPhone && adopterPhone === normalizePhoneDigits(existingContact.primaryPhone);

        if (!emailMatches && !phoneMatches) {
          const existingName =
            [existingContact.firstName, existingContact.lastName].filter(Boolean).join(' ').trim() || 'someone else';
          const adopterLabel = adopter.fullName?.trim() || adopter.email || 'the pending adopter';
          setSerialError(
            `This chip is registered to ${existingName} (${existingContact.primaryEmail ?? 'no email'}), ` +
              `which doesn't match ${adopterLabel}. Verify with the adopter or recall the chip first.`,
          );
          return;
        }
      }

      // Step 3: Claim the chip if it belongs to another reseller and clear
      // any prior owner contact atomically. `clearOwnerInfo: true` resets the
      // chip's `pet` link and contact history without touching any pet's
      // household — unlike `recallChip`, which calls
      // `getFirstHouseholdForUser(resellerUser)` and would yank a pending-
      // adoption pet out of its adoption household. The chip's owner is
      // re-established naturally when the adopter accepts the household
      // invite (policies.isChipOwner resolves it via the pet's household).
      let chipId = graphqlId;
      if (needsClaim) {
        const { data: claimData } = await reassignToReseller({
          variables: { input: { serialNumber: data.resellerChip.serialNumber, clearOwnerInfo: true } },
        });
        chipId = claimData?.reassignChipToReseller?.id ?? chipId;
      }

      // Step 4: Assign existing pet to the chip
      await assignExistingPet({ variables: { input: { chipId, petId: pet.id } } });
      showToast(`Chip ${trimmed} added to ${pet.name}`);
      handleClose();
    } catch (err) {
      setSerialError(err instanceof Error ? err.message : 'Failed to add chip');
    } finally {
      setSubmitting(false);
    }
  }

  if (!pet) {
    return null;
  }

  const canSubmit = serialNumber.trim().length > 0 && !submitting;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add Chip to {pet.name}</ModalTitle>
      {pet.breed?.name && <p className={styles.subtitle}>{pet.breed.name}</p>}

      <PortalField label="Chip Serial Number" error={serialError} showError={!!serialError}>
        <PortalInput
          value={serialNumber}
          onChange={(e) => {
            setSerialNumber(e.target.value);
            setSerialError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
          placeholder="e.g. 985112345678900"
          hasError={!!serialError}
          autoFocus
        />
      </PortalField>

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Adding...' : `Add Chip to ${pet.name}`}
        </Button>
      </ModalActions>
    </Modal>
  );
}
